import $ from 'jquery'
import _ from 'lodash'

import ace from 'ace-builds'

import 'ace-builds/webpack-resolver'
import 'ace-builds/src-noconflict/ace'
import 'ace-builds/src-noconflict/mode-liquid'
import 'ace-builds/src-noconflict/theme-chrome'
import 'ace-builds/src-noconflict/ext-language_tools'
import 'ace-builds/src-noconflict/ext-searchbox'

import './sf-liquid'
import { getNavbarHeight } from '@app/utils/domHelpers'

export default class SfAceEditor {
  static initialize($dom) {
    return $dom.find("[data-behaviour='sf-ace-editor']").each(function () {
      return new SfAceEditor(this)
    })
  }

  constructor(element) {
    this.makeBold = this.makeBold.bind(this)
    this.makeItalic = this.makeItalic.bind(this)
    this.makeUnderlined = this.makeUnderlined.bind(this)
    this.insertOrderedList = this.insertOrderedList.bind(this)
    this.insertUnorderedList = this.insertUnorderedList.bind(this)
    this.insertTable = this.insertTable.bind(this)
    this.showPreview = this.showPreview.bind(this)
    this.hidePreview = this.hidePreview.bind(this)
    this.showDiff = this.showDiff.bind(this)
    this.hideDiff = this.hideDiff.bind(this)
    this.verify = this.verify.bind(this)
    this.setVerifyBadge = this.setVerifyBadge.bind(this)
    this.clearVerifyBadge = this.clearVerifyBadge.bind(this)
    this.element = $(element)
    this.defineElements()
    this.defineEditor()
    this.togglePreview()
    this.toggleDiff()
    this.initializeListeners()
  }

  defineElements() {
    this.TOP_SCROLL_MARGIN = 10
    this.MIN_LINES = 5
    this.VERIFY_DELAY = 3000

    this.input = this.element.find('textarea')
    this.originalValue = this.input.val()
    this.editorElement = $(
      "<div class='sf-ace-editor-container'></div>"
    ).appendTo(this.element)
    this.input.hide()

    this.context = this.input.data('context')
    this.verifyUrl = this.input.data('verify-url')
    this.verifyIcon = this.element.find("[data-action='verify'] i")
    this.autoVerify = _.debounce(() => {
      this.verify(false)
    }, this.VERIFY_DELAY)

    this.autocompleteUrl = this.input.data('autocomplete-url')
    this.previewUrl = this.input.data('preview-url')
    this.diffUrl = this.input.data('diff-url')
    this.previewContainer = this.element.find(
      "[data-behaviour='sf-ace-editor-preview']"
    )
    return (this.diffContainer = this.element.find(
      "[data-behaviour='sf-ace-editor-diff']"
    ))
  }

  defineEditor() {
    window.ace.require('ace/ext/language_tools')
    this.editor = window.ace.edit(this.editorElement[0])

    $(document).one('turbolinks:before-render', () => {
      this.editor.destroy()
      const element = this.editor.container
      return element.parentNode.removeChild(element)
    })

    this.editor.setValue(this.originalValue, -1)
    if (this.input.attr('autofocus')) {
      this.editor.focus()
    }
    this.editor.getSession().setMode('ace/mode/sf_liquid')
    this.editor.getSession().setUseWrapMode(true)
    this.editor.getSession().setTabSize(2)
    this.editor.setTheme('ace/theme/chrome')
    this.editor.renderer.setShowGutter(true)
    this.editor.renderer.setShowPrintMargin(false)
    this.editor.renderer.setScrollMargin(
      this.TOP_SCROLL_MARGIN,
      this.TOP_SCROLL_MARGIN,
      0,
      0
    )
    this.editor.setHighlightActiveLine(true)
    this.editor.setBehavioursEnabled(true)
    this.editor.setOptions({
      enableBasicAutocompletion: true,
      enableSnippets: true,
    })

    this.editor.commands.addCommand({
      name: 'underline',
      bindKey: {
        win: 'Ctrl-U',
        mac: 'Command-U',
        sender: '@editor|cli',
      },
      exec: (_env, _args, _request) => {
        return this.makeUnderlined()
      },
    })

    this.editor.commands.addCommand({
      name: 'italic',
      bindKey: {
        win: 'Ctrl-I',
        mac: 'Command-I',
        sender: '@editor|cli',
      },
      exec: (_env, _args, _request) => {
        return this.makeItalic()
      },
    })

    this.editor.commands.addCommand({
      name: 'bold',
      bindKey: {
        win: 'Ctrl-B',
        mac: 'Command-B',
        sender: '@editor|cli',
      },
      exec: (_env, _args, _request) => {
        return this.makeBold()
      },
    })

    this.editor.commands.addCommand({
      name: 'verify',
      bindKey: {
        win: 'Ctrl-Enter',
        mac: 'Command-Enter',
        sender: '@editor|cli',
      },
      exec: (_env, _args, _request) => {
        this.autoVerify.cancel()
        return this.verify()
      },
    })

    this.editor.on('change', this.autoVerify)

    this.editor.getSession().on('change', () => {
      this.input.val(this.editor.getValue())
      return this.input.trigger('change')
    })

    this.editor.session.setScrollTop(-this.TOP_SCROLL_MARGIN)

    const session = this.editor.getSession()
    const undoManager = session.getUndoManager()
    undoManager.reset()
    session.setUndoManager(undoManager)

    const { Range } = ace.require('ace/range')
    const tokenRe = new RegExp('^[a-zA-Z0-9#_]+', 'g')

    const resizeEditor = () => {
      this.editorElement.css(
        'height',
        Math.min(
          this.editor.renderer.lineHeight *
            Math.max(
              this.editor.getSession().getScreenLength(),
              this.MIN_LINES
            ) +
            2 * this.TOP_SCROLL_MARGIN,
          $(window).height() - getNavbarHeight()
        )
      )
      return this.editor.resize()
    }

    if (this.input.attr('data-autoresize')) {
      this.editor.on('change', resizeEditor)
      resizeEditor()
    }

    this.editor.commands.on('afterExec', (e) => {
      if (e.command.name !== 'insertstring') {
        return
      }

      const currentSelection = this.editor.selection.getCursor()
      const { row } = currentSelection
      const { column } = currentSelection

      const line = this.editor.session.getLine(row)

      let start = column
      if (start > 0) {
        start--
        while (start >= 0 && line.charAt(start).match(tokenRe)) {
          start--
        }
        start++
      }

      let end = column
      while (end < line.length && line.charAt(end).match(tokenRe)) {
        end++
      }

      const currentWord = this.editor.session.getTextRange(
        new Range(row, start, row, end)
      )
      if (currentWord[0] === '#') {
        return this.editor.execCommand('startAutocomplete')
      }
    })

    ace.require('ace/autocomplete') // enough to enable autocompletion

    const { autocompleteUrl } = this
    if (!autocompleteUrl) {
      return
    }

    const groupCompleter = {
      getCompletions(editor, session, pos, prefix, callback) {
        if (prefix.length === 0) {
          return callback(null, [])
        } else {
          return $.get(
            autocompleteUrl,
            { cache: true },
            (data) =>
              callback(
                null,
                data.map((item) => ({
                  caption: item.caption,
                  snippet: item.snippet,

                  completer: {
                    insertMatch(editor) {
                      const { filterText } = editor.completer.completions
                      if (filterText) {
                        const ranges = editor.selection.getAllRanges()
                        for (const range of Array.from(ranges)) {
                          ;(function (range) {
                            range.start.column -= filterText.length
                            return editor.session.remove(range)
                          })(range)
                        }
                      }
                      return editor.insert(item.snippet)
                    },
                  },
                }))
              ),
            'json'
          ).fail(() => callback(null, []))
        }
      },
    }
    return this.editor.completers.push(groupCompleter)
  }

  surroundSelectionWith(surroundString, defaultText) {
    if (defaultText === null) {
      defaultText = ''
    }
    const currentText =
      this.editor.session.getTextRange(this.editor.getSelectionRange()) ||
      defaultText
    const newText = `${surroundString}${currentText}${surroundString}`
    const previousRange = this.editor.selection.getRange()
    this.editor.session.replace(previousRange, newText)
    previousRange.start.column += surroundString.length
    if (previousRange.start.row === previousRange.end.row) {
      previousRange.end.column = previousRange.start.column + currentText.length
    }
    this.editor.selection.setSelectionRange(previousRange)
    return this.editor.focus()
  }

  insertAndSelectWithPrefix(string, prefix) {
    this.editor.insert(`${prefix}${string}`)
    const previousRange = this.editor.selection.getRange()
    previousRange.start.column -= string.length
    this.editor.selection.setSelectionRange(previousRange)
    return this.editor.focus()
  }

  makeBold() {
    return this.surroundSelectionWith('**', 'bold text')
  }

  makeItalic() {
    return this.surroundSelectionWith('*', 'italic text')
  }

  makeUnderlined() {
    return this.surroundSelectionWith('_', 'underlined text')
  }

  insertOrderedList() {
    return this.insertAndSelectWithPrefix('list item', '1. ')
  }

  insertUnorderedList() {
    return this.insertAndSelectWithPrefix('list item', '* ')
  }

  insertTable() {
    this.editor.insert('\n')
    const { snippetManager } = ace.require('ace/snippets')
    const snippet = snippetManager.getSnippetByName('|table', this.editor)
    if (snippet) {
      snippetManager.insertSnippet(this.editor, snippet.content)
    }
    return this.editor.focus()
  }

  verify(manual = true) {
    if (!this.verifyUrl) return

    Silverfin.Spinner.start()
    this.editor.getSession().clearAnnotations()

    $.post(
      this.verifyUrl,
      { code: this.editor.getValue(), context: this.context },
      (result) => {
        if ($.isEmptyObject(result)) {
          this.clearVerifyBadge()
          Silverfin.Spinner.success()
        } else {
          this.setVerifyBadge(result.length)
          this.editor.getSession().setAnnotations(result)
          const annotation =
            result.find((a) => a['type'] === 'error') || result[0]
          const line = (annotation['row'] || 0) + 1

          if (annotation['type'] === 'error') {
            Silverfin.Spinner.failed()
          } else {
            Silverfin.Spinner.warning()
          }

          if (manual) {
            this.editor.gotoLine(line)
            this.editor.focus()
          }
        }
      }
    )
    return
  }

  setVerifyBadge(value) {
    this.clearVerifyBadge()
    return $('<div class="sf-icon-badge">' + value + '</div>').appendTo(
      this.verifyIcon
    )
  }

  clearVerifyBadge() {
    return this.verifyIcon.find('.sf-icon-badge').remove()
  }

  showPreview() {
    if (!this.previewContainer) {
      return
    }
    return $.post(
      this.previewUrl,
      { markdown: this.editor.getValue() },
      (data) => {
        this.previewContainer.html(data.html).show()
        return (this.previewVisible = true)
      }
    )
  }

  hidePreview() {
    if (!this.previewContainer) {
      return
    }
    this.previewContainer.html('').hide()
    this.previewVisible = false
    return this.editor.focus()
  }

  showDiff() {
    if (!this.diffContainer) {
      return
    }

    const oldText = this.originalValue
    const newText = this.editor.getValue()

    if (oldText === newText) {
      this.diffContainer
        .html('<div class="no-diff">No changes to show.</p>')
        .show()
      return (this.diffVisible = true)
    }

    return $.post(
      this.diffUrl,
      { old_text: oldText, new_text: newText },
      (data) => {
        this.diffContainer.html(data.html).show()
        return (this.diffVisible = true)
      }
    )
  }

  hideDiff() {
    if (!this.diffContainer) {
      return
    }
    this.diffContainer.html('').hide()
    this.diffVisible = false
    return this.editor.focus()
  }

  togglePreview() {
    if (this.previewVisible) {
      return this.showPreview()
    } else {
      return this.hidePreview()
    }
  }

  toggleDiff() {
    if (this.diffVisible) {
      return this.showDiff()
    } else {
      return this.hideDiff()
    }
  }

  initializeListeners() {
    this.element.on(
      'click',
      "[data-behaviour='sf-ace-editor-action']",
      (event) => {
        return this[$(event.currentTarget).data('action')]()
      }
    )

    this.element.on('click', "[data-behaviour='sf-ace-editor-preview']", () => {
      return this.hidePreview()
    })

    return this.element.on(
      'click',
      "[data-behaviour='sf-ace-editor-diff']",
      () => {
        return this.hideDiff()
      }
    )
  }
}
